import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Loader2, CreditCard, CheckCircle2, AlertCircle, Lock, Droplets } from "lucide-react";
import { toast } from "sonner";
import CardknoxPayDialog from "@/components/CardknoxPayDialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
const formatErr = (d) => (typeof d === "string" ? d : (d?.detail || d?.message || "Something went wrong."));

/**
 * Public (no-login) order review + pay page.
 *
 * Customers land here from the "Review & pay" link in the order-placed
 * email. The `token` in the URL is the only auth — there is no JWT or
 * cookie required. Once paid the token is invalidated server-side so the
 * link can't be reused.
 */
export default function OrderPay() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, order: null, cof: null, business: "", err: null, paid: false });
  const [payOpen, setPayOpen] = useState(false);
  const [chargingSaved, setChargingSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND}/api/orders/by-pay-token/${token}`);
      setState({
        loading: false,
        order: r.data.order,
        cof: r.data.card_on_file,
        business: r.data.business_name,
        err: null,
        paid: r.data.order?.status !== "placed",
      });
    } catch (e) {
      setState({ loading: false, order: null, cof: null, business: "", paid: false,
        err: formatErr(e.response?.data?.detail) || e.message });
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const payWithCardOnFile = async () => {
    if (!state.cof?.last4) return;
    if (!window.confirm(`Pay ${fmt(state.order.total)} with card ending in ${state.cof.last4}?`)) return;
    setChargingSaved(true);
    try {
      await axios.post(`${BACKEND}/api/orders/by-pay-token/${token}/charge-saved`);
      toast.success("Payment received — thank you!");
      setState((s) => ({ ...s, paid: true }));
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setChargingSaved(false); }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" />
          <p className="text-sm text-muted-foreground mt-2">Loading your order…</p></div>
      </div>
    );
  }

  if (state.err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="text-center max-w-md" data-testid="order-pay-error">
          <AlertCircle className="w-10 h-10 mx-auto text-destructive mb-3" />
          <p className="overline text-destructive mb-2">Link unavailable</p>
          <p className="text-sm text-muted-foreground mb-6">{state.err}</p>
          <Link to="/login" className="text-sm underline">Sign in to your portal instead</Link>
        </div>
      </div>
    );
  }

  if (state.paid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="text-center max-w-md" data-testid="order-pay-success">
          <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
          <p className="overline mb-1">Payment received</p>
          <h1 className="font-display text-2xl font-bold mb-2">Order {state.order?.number} is paid</h1>
          <p className="text-sm text-muted-foreground mb-6">
            Thank you! We&apos;ll start fulfilling your order shortly and follow up with delivery details.
          </p>
          <Link to="/login" className="text-sm underline">View this order in your portal →</Link>
        </div>
      </div>
    );
  }

  const o = state.order;
  const items = o.items || [];

  return (
    <div className="min-h-screen bg-background py-10 px-4">
      <div className="max-w-2xl mx-auto" data-testid="order-pay-screen">
        <div className="flex items-center gap-2 mb-6">
          <Droplets className="w-6 h-6 text-primary" />
          <p className="overline">{state.business}</p>
        </div>

        <header className="border-b border-border pb-4 mb-6">
          <p className="overline mb-1">Order placed — payment requested</p>
          <h1 className="font-display text-3xl font-bold tracking-tight">{o.number}</h1>
        </header>

        <section className="border border-border rounded-sm p-4 mb-4 bg-card">
          <p className="overline mb-3">Items</p>
          <div className="divide-y divide-border">
            {items.map((it, i) => (
              <div key={`item-${i}-${it.product_id || it.description}`} className="flex items-center justify-between py-2 text-sm">
                <div className="flex-1">
                  <p className="font-medium">{it.description}</p>
                </div>
                <div className="font-mono text-xs text-muted-foreground w-12 text-center">×{Number(it.quantity || 0)}</div>
                <div className="font-mono font-medium w-24 text-right">{fmt(it.amount)}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-border space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">{fmt(o.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Tax</span><span className="font-mono">{fmt(o.tax)}</span></div>
            <div className="flex justify-between text-base pt-2 border-t border-border mt-2"><span className="font-bold">Total due</span><span className="font-mono font-bold" data-testid="order-pay-total">{fmt(o.total)}</span></div>
          </div>
        </section>

        <div className="flex flex-col gap-3" data-testid="order-pay-actions">
          {state.cof?.last4 && (
            <Button
              size="lg"
              className="rounded-sm h-12 font-semibold"
              disabled={chargingSaved}
              onClick={payWithCardOnFile}
              data-testid="order-pay-cof-button"
            >
              {chargingSaved
                ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Charging…</>)
                : (<><CreditCard className="w-4 h-4 mr-2" /> Pay {fmt(o.total)} with •••• {state.cof.last4}</>)}
            </Button>
          )}
          <Button
            size="lg"
            variant={state.cof?.last4 ? "outline" : "default"}
            className="rounded-sm h-12 font-semibold"
            onClick={() => setPayOpen(true)}
            data-testid="order-pay-new-card-button"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            {state.cof?.last4 ? "Use a different card" : `Pay ${fmt(o.total)}`}
          </Button>
          <p className="text-xs text-muted-foreground flex items-center gap-1 justify-center">
            <Lock className="w-3 h-3" /> Card data is sent directly to Cardknox — {state.business} never sees your card number.
          </p>
        </div>

        <CardknoxPayDialog
          open={payOpen}
          onClose={() => setPayOpen(false)}
          invoice={{ id: token, number: o.number, total: o.total }}
          mode="pay-order-public"
          payToken={token}
          onPaid={() => { setPayOpen(false); setState((s) => ({ ...s, paid: true })); }}
        />
      </div>
    </div>
  );
}
